/**
 * Cache utility with TTL (Time To Live) for optimizing API calls
 * Uses sessionStorage for automatic cleanup on browser close
 */

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

export const cache = {
  /**
   * Store data in cache with timestamp
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} ttl - Time to live in milliseconds (optional)
   */
  set: (key, data, ttl = DEFAULT_TTL) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        ttl
      };
      sessionStorage.setItem(key, JSON.stringify(cacheData));
    } catch (e) {
      console.warn('Cache set error:', e);
    }
  },

  /**
   * Get data from cache if not expired
   * @param {string} key - Cache key
   * @returns {any|null} Cached data or null if expired/not found
   */
  get: (key) => {
    try {
      const cached = sessionStorage.getItem(key);
      if (!cached) return null;
      
      const { data, timestamp, ttl } = JSON.parse(cached);
      const age = Date.now() - timestamp;
      
      if (age > ttl) {
        sessionStorage.removeItem(key);
        return null;
      }
      
      return data;
    } catch (e) {
      console.warn('Cache get error:', e);
      return null;
    }
  },

  /**
   * Check if a key exists and is not expired
   * @param {string} key - Cache key
   * @returns {boolean}
   */
  has: (key) => {
    return cache.get(key) !== null;
  },

  /**
   * Remove specific cache entry
   * @param {string} key - Cache key
   */
  remove: (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      console.warn('Cache remove error:', e);
    }
  },

  /**
   * Clear cache entries matching a pattern
   * @param {string} pattern - Pattern to match (substring)
   */
  clear: (pattern) => {
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.includes(pattern)) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn('Cache clear error:', e);
    }
  },

  /**
   * Clear all cache entries
   */
  clearAll: () => {
    try {
      // Only clear our cache entries, not other session storage
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        // Clear cache entries (they have specific patterns)
        if (key.includes('grades-') || 
            key.includes('class-') || 
            key.includes('semesters-') || 
            key.includes('subjects-') ||
            key.includes('student-') ||
            key.includes('admin-')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn('Cache clearAll error:', e);
    }
  },

  /**
   * Get cache statistics
   * @returns {object} Cache statistics
   */
  stats: () => {
    try {
      const keys = Object.keys(sessionStorage);
      const cacheKeys = keys.filter(key => 
        key.includes('grades-') || 
        key.includes('class-') || 
        key.includes('semesters-') || 
        key.includes('subjects-')
      );
      
      let totalSize = 0;
      let expired = 0;
      
      cacheKeys.forEach(key => {
        const value = sessionStorage.getItem(key);
        if (value) {
          totalSize += value.length;
          try {
            const { timestamp, ttl } = JSON.parse(value);
            if (Date.now() - timestamp > ttl) {
              expired++;
            }
          } catch (e) {
            // Invalid cache entry
          }
        }
      });
      
      return {
        totalEntries: cacheKeys.length,
        expiredEntries: expired,
        approximateSizeKB: (totalSize / 1024).toFixed(2)
      };
    } catch (e) {
      return { error: e.message };
    }
  }
};

/**
 * Cache key generators for consistent naming
 */
export const cacheKeys = {
  grades: (studentId) => `grades-${studentId}`,
  class: (classId) => `class-${classId}`,
  semesters: (schoolId, classId) => `semesters-${schoolId}-${classId}`,
  allSemesters: () => `semesters-all`,
  subjects: (schoolId, classId, semesterId) => `subjects-${schoolId}-${classId}-${semesterId}`,
  allSubjects: () => `subjects-all`,
  student: (studentId) => `student-${studentId}`,
  myClasses: (userId) => `my-classes-${userId}`,
  // Admin dashboard cache keys
  USERS: 'admin-users',
  CLASSES: 'admin-classes',
  SEMESTERS: 'admin-semesters',
  DASHBOARD_DATA: 'admin-dashboard-data',
  STUDENT_GRADES: (studentId) => `admin-student-grades-${studentId}`
};

/**
 * Invalidate related caches when data changes
 */
export const invalidateCache = {
  student: (studentId) => {
    cache.clear(`student-${studentId}`);
    cache.clear(`grades-${studentId}`);
  },
  class: (classId) => {
    cache.remove(`class-${classId}`);
    cache.clear(`semesters-`); // Clear all semester caches
  },
  grades: (studentId) => {
    cache.remove(`grades-${studentId}`);
  },
  subjects: () => {
    cache.clear(`subjects-`);
  }
};

export default cache;
